/* eslint-disable react/prop-types */
/* eslint-disable complexity */
import React, { useState } from 'react';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';

import ImageUpload, { DEFAULT_IMAGE_URL } from 'components/ImageUpload';
import restService from 'services/restService';

const RenderFields = ({ prayer, onSuccess }) => {
  const [prayerText, setPrayerText] = useState(prayer?.prayer?.prayerText);
  const [heading, setHeading] = useState(prayer?.spotlight?.heading);
  const [subheading, setSubheading] = useState(prayer?.spotlight?.subheading);
  const [buttonText, setButtonText] = useState(prayer?.spotlight?.buttonText);
  const [imageUrl, setImageUrl] = useState(prayer?.spotlight?.backgroundImage);
  const [startOn, setStartOn] = useState(
    prayer?.spotlight?.startOn ? dayjs(prayer?.spotlight?.startOn) : undefined
  );
  const [endOn, setEndOn] = useState(
    prayer?.spotlight?.endOn ? dayjs(prayer?.spotlight?.endOn) : undefined
  );

  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [hasFailedToUpdated, setHasFailedToUpdate] = useState(false);

  const [hasUpdatedAtLeastOneField, updatedFields] = [
    ['Prayer Text', 'prayerText', prayerText, prayer?.prayer?.prayerText],
    ['Heading', 'heading', heading, prayer?.spotlight?.heading],
    ['Subheading', 'subheading', subheading, prayer?.spotlight?.subheading],
    ['Button Text', 'buttonText', buttonText, prayer?.spotlight?.buttonText],
    ['Image URL', 'backgroundImage', imageUrl, prayer?.spotlight?.backgroundImage],
    [
      'Start On',
      'startOn',
      startOn,
      prayer?.spotlight?.startOn ? dayjs(prayer?.spotlight?.startOn) : undefined,
    ],
    [
      'End On',
      'endOn',
      endOn,
      prayer?.spotlight?.endOn ? dayjs(prayer?.spotlight?.endOn) : undefined,
    ],
  ].reduce(
    (p, [readableName, fieldName, currentValue, stateValue]) => {
      if (currentValue !== stateValue) {
        if (dayjs.isDayjs(currentValue)) {
          // eslint-disable-next-line max-depth
          if (!currentValue.isSame(stateValue)) {
            p[1].push({
              readableName,
              fieldName,
              value: currentValue,
            });
            p[0] = true;
          }
        } else {
          p[1].push({
            readableName,
            fieldName,
            value: currentValue,
          });
          p[0] = true;
        }
      }
      return p;
    },
    [false, []]
  );

  const handlePrayerTextChange = (event) => {
    setPrayerText(event.target.value);
  };

  const handleHeadingChange = (event) => {
    setHeading(event.target.value);
  };

  const handleSubheadingChange = (event) => {
    setSubheading(event.target.value);
  };

  const handleButtonTextChange = (event) => {
    setButtonText(event.target.value);
  };
  const handleStartOnChange = (event) => setStartOn(event);
  const handleEndOnChange = (event) => setEndOn(event);

  const onSubmit = () => {
    setOpenConfirmationDialog((p) => !p);
  };

  const onSubmitConfirmation = () => {
    const updatedFieldsPayload = updatedFields.reduce((p, c) => {
      p[c.fieldName] = c.value;
      return p;
    }, {});
    restService
      .callEndpoint({
        endpoint: 'updateCrossroadsPrayer',
        body: {
          prayerId: prayer.prayer._id,
          action: 'update',
          updatedFields: updatedFieldsPayload,
        },
        method: 'post',
      })
      .then((response) => {
        if (!response || response?.status === 'failed') {
          setHasFailedToUpdate(true);
        } else {
          onSuccess?.();
        }
      })
      .catch(() => {
        setHasFailedToUpdate(true);
      });
  };

  const renderEmptyImageUrlMessage = () => (
    <div>
      <Typography sx={{ color: 'text.primary' }} variant="body1">
        {
          'No image selected. Click "Upload Image" below. If you do not select an image we\'ll use'
        }
      </Typography>
      <Link
        sx={{ color: 'text.primary' }}
        target="_blank"
        rel="noopener"
        href={DEFAULT_IMAGE_URL}
      >
        {' this default image.'}
      </Link>
    </div>
  );

  const DialogBody = () => {
    if (!hasFailedToUpdated) {
      return (
        <>
          <DialogTitle id="alert-dialog-title">{'Update Content'}</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              The following fields have been modified:
              {updatedFields.map(({ readableName }) => (
                <p style={{ margin: '0px' }} key={readableName}>
                  {readableName}
                </p>
              ))}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={onSubmit}>Go Back</Button>
            <Button onClick={onSubmitConfirmation} autoFocus>
              Update Content
            </Button>
          </DialogActions>
        </>
      );
    }
    return (
      <>
        <DialogTitle id="alert-dialog-title">{'Error'}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            An error has occurred. Please try again in a few moments.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenConfirmationDialog(false);
              setTimeout(() => {
                setHasFailedToUpdate(false);
              }, 250);
            }}
          >
            Ok
          </Button>
        </DialogActions>
      </>
    );
  };

  return (
    <>
      <Box
        component="form"
        noValidate
        autoComplete="off"
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: 2,
        }}
      >
        <TextField
          multiline
          fullWidth
          required
          label="Prayer Text"
          value={prayerText}
          onChange={handlePrayerTextChange}
        />
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: 2,
          }}
        >
          <TextField
            required
            label="Heading"
            value={heading}
            onChange={handleHeadingChange}
          />
          <TextField
            label="Subheading"
            value={subheading}
            onChange={handleSubheadingChange}
          />
          <TextField
            label="Button Text"
            value={buttonText}
            onChange={handleButtonTextChange}
          />
          <DateTimePicker
            label="Start On"
            value={startOn}
            onChange={handleStartOnChange}
          />
          <DateTimePicker label="End On" value={endOn} onChange={handleEndOnChange} />
        </Box>
        <ImageUpload
          setImageUrl={setImageUrl}
          previewUrl={imageUrl}
          renderEmpty={renderEmptyImageUrlMessage}
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          fontSize: 20,
          fontWeight: 'bold',
          cursor: 'pointer',
          marginTop: '40px',
        }}
      >
        <Button
          variant="contained"
          onClick={onSubmit}
          disabled={!hasUpdatedAtLeastOneField}
        >
          Save Content
        </Button>
      </Box>
      <Dialog
        open={openConfirmationDialog}
        onClose={onSubmit}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogBody />
      </Dialog>
    </>
  );
};

export default function EditPrayer({ prayer, handleClose, onSuccess }) {
  const onEditSuccess = () => {
    onSuccess?.();
    handleClose?.();
  };

  return (
    <Box
      sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '80%',
        height: '70%',
        bgcolor: 'background.paper',
        border: '2px solid #000',
        p: 4,
        overflowY: 'scroll',
        scrollbarColor: 'gray #1C1C1E',
        scrollbarWidth: 'thin',
      }}
    >
      <Button
        variant="contained"
        onClick={handleClose}
        style={{
          float: 'right',
          fontSize: 15,
          fontWeight: 'bold',
          cursor: 'pointer',
        }}
      >
        X
      </Button>
      <Typography
        id="modal-modal-title"
        variant="h3"
        component="h2"
        sx={{ marginBottom: '20px', color: 'text.primary' }}
      >
        {'Edit Crossroads Prayer'}
      </Typography>
      <RenderFields prayer={prayer} onSuccess={onEditSuccess} />
    </Box>
  );
}
